// Scale bridge dashboard - app shell.
//
// Dark sidebar with a sectioned menu and a brand mark, a header carrying the
// page title and the connection state, and the routed screen below. The connect
// screen replaces the layout entirely until a receiver and view key are stored.
//
// ─── The scroll model ──────────────────────────────────────────────────
// The shell is 100vh and does not scroll. The CONTENT pane is the only scroller,
// which is what keeps the sidebar and the header in place while a long weighings
// table moves underneath them. Done with flex rather than position: fixed, so
// there is no left-margin to keep in sync with the sidebar width and no second
// scrollbar when the sidebar is taller than the window.

import { useEffect, useMemo, useState } from 'react';
import {
    HashRouter,
    NavLink,
    Navigate,
    Route,
    Routes,
    useLocation,
} from 'react-router-dom';
import { Alert, App as AntApp, Avatar, Button, ConfigProvider, Dropdown, Layout, Menu, Space, Tooltip, type MenuProps } from 'antd';
import {
    ApiOutlined,
    DownOutlined,
    LogoutOutlined,
    MenuFoldOutlined,
    MenuUnfoldOutlined,
} from '@ant-design/icons';

import Overview from './screens/Overview';
import Readings from './screens/Readings';
import Devices from './screens/Devices';
import Settings from './screens/Settings';
import Login from './screens/Login';

import { useAuthStore } from './state/authStore';
import { REFRESH_MS, useReadingsStore } from './state/readingsStore';
import { startLiveFeed } from './live/socket';
import { clearSession } from './api/client';
import type { Reading } from './types';
import { relative } from './lib/format';
import { BRAND, theme } from './theme';
import { NAV_SECTIONS, SCROLL_CONTAINER_ID, navItemFor } from './nav';

const { Header, Sider, Content } = Layout;

/**
 * Whether the receiver is reachable, and whether the feed is genuinely live.
 *
 * "Live" is a stronger claim than "Connected" and is only made when a socket is
 * actually open - during a reconnect the screen is still updating on the poll,
 * and saying "Live" then would overstate how fresh it is.
 *
 * Colour is not the only signal: the word says the same thing as the dot, and
 * the tooltip carries the time of the last update either way.
 */
function ConnectionPill() {
    const error = useReadingsStore((s) => s.error);
    const unauthorized = useReadingsStore((s) => s.unauthorized);
    const lastRefreshAt = useReadingsStore((s) => s.lastRefreshAt);
    const liveStatus = useReadingsStore((s) => s.liveStatus);

    const bad = Boolean(error) || unauthorized;
    const live = !bad && liveStatus === 'live';

    const label = unauthorized
        ? 'Key rejected'
        : error
          ? 'Receiver unreachable'
          : live
            ? 'Live'
            : 'Connected';

    // Said in full in the tooltip, because the pill itself is two words at most
    // and an operator deciding whether to trust a number on screen needs to know
    // how old it is.
    const detail = bad
        ? 'The screen is showing the last data that loaded.'
        : live
          ? 'Weighings appear here as they are printed.'
          : `Updating every ${Math.round(REFRESH_MS / 1000)}s.`;
    const freshness = lastRefreshAt
        ? `Last update ${relative(new Date(lastRefreshAt).toISOString())}`
        : 'No update yet';

    return (
        <Tooltip title={`${freshness}. ${detail}`}>
            <span
                className={`sb-conn${bad ? ' sb-conn--bad' : ''}${live ? ' sb-conn--live' : ''}`}
                role="status"
            >
                <span className="sb-conn__dot" />
                <span className="sb-conn__label">{label}</span>
            </span>
        </Tooltip>
    );
}

function Shell() {
    const location = useLocation();
    const session = useAuthStore((s) => s.session)!;
    const refresh = useReadingsStore((s) => s.refresh);
    const upsert = useReadingsStore((s) => s.upsert);
    const setLiveStatus = useReadingsStore((s) => s.setLiveStatus);
    const error = useReadingsStore((s) => s.error);
    const [collapsed, setCollapsed] = useState(false);

    // One interval for the whole app, owned here. Every screen reads the same
    // store, so a per-screen poll would multiply requests and let two screens
    // disagree about what "now" is.
    useEffect(() => {
        void refresh(session);
        const id = window.setInterval(() => void refresh(session), REFRESH_MS);
        return () => window.clearInterval(id);
    }, [refresh, session]);

    // ─── Phase 4: the live feed ─────────────────────────────────────────
    // The poll above is NOT removed by this. A socket bumps the list; it does not
    // recompute the server-side aggregates behind the KPI strip, so the periodic
    // refresh is what keeps those numbers moving.
    useEffect(() => {
        const feed = startLiveFeed(session, {
            onReading: (reading) => upsert(reading as Reading),
            // Every reconnect re-hydrates. Whatever happened during the gap was
            // never delivered, and no amount of socket correctness recovers it.
            onResync: () => void refresh(session),
            onStatus: setLiveStatus,
            onSessionEnded: () => {
                // The socket already asked REST whether the session was really over
                // before deciding this - so an expired licence does not present as a
                // reconnect loop.
                clearSession();
                window.location.reload();
            },
        });
        return () => feed.stop();
    }, [session, refresh, upsert, setLiveStatus]);

    const page = useMemo(() => navItemFor(location.pathname), [location.pathname]);

    // One Menu rather than one per section: two menus are two independent roving
    // tab stops, so a keyboard user tabbing through the sidebar leaves the first
    // group and lands outside the nav instead of in the second.
    //
    // Collapsed, the group titles go and a divider takes their place. Ant Design
    // renders a group's title as a truncated stub in the icon-only rail, which is
    // three grey dots where a heading used to be.
    const menuItems: MenuProps['items'] = useMemo(() => {
        const toItem = (i: (typeof NAV_SECTIONS)[number]['items'][number]) => ({
            key: i.key,
            icon: i.icon,
            label: <NavLink to={i.path}>{i.label}</NavLink>,
        });

        if (collapsed) {
            return NAV_SECTIONS.flatMap((section, idx) =>
                idx === 0
                    ? section.items.map(toItem)
                    : [{ type: 'divider' as const, key: `d-${section.key}` }, ...section.items.map(toItem)],
            );
        }

        return NAV_SECTIONS.map((section) => ({
            key: section.key,
            type: 'group' as const,
            label: section.title,
            children: section.items.map(toItem),
        }));
    }, [collapsed]);

    return (
        <Layout className="sb-shell">
            <Sider
                className="sb-sider"
                width={232}
                collapsedWidth={72}
                collapsible
                collapsed={collapsed}
                onCollapse={setCollapsed}
                // The trigger lives in the header instead, next to the title it
                // affects, rather than as a bar welded to the bottom of the screen.
                trigger={null}
                theme="dark"
            >
                <div className="sb-sider-brand">
                    <div className="sb-sider-brand__mark">
                        <ApiOutlined />
                    </div>
                    {!collapsed && (
                        <div style={{ minWidth: 0 }}>
                            <div className="sb-sider-brand__name">{BRAND.name}</div>
                            <div className="sb-sider-brand__sub">{BRAND.tagline}</div>
                        </div>
                    )}
                </div>

                <div className="sb-sider-nav">
                    <Menu
                        theme="dark"
                        mode="inline"
                        inlineCollapsed={collapsed}
                        selectedKeys={[page.key]}
                        items={menuItems}
                    />
                </div>

                {/* Which shop's numbers are on screen. On a machine that can be
                    re-activated against a different weighbridge, this is the fact
                    that stops somebody reading the wrong site's day. */}
                {!collapsed && session.accountName && (
                    <div className="sb-sider-foot" title={session.accountName}>
                        <div style={{ textOverflow: 'ellipsis', overflow: 'hidden' }}>
                            {session.accountName}
                        </div>
                    </div>
                )}
            </Sider>

            <Layout style={{ minWidth: 0 }}>
                <Header className="sb-header">
                    <Button
                        type="text"
                        aria-label={collapsed ? 'Expand the sidebar' : 'Collapse the sidebar'}
                        icon={collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
                        onClick={() => setCollapsed((c) => !c)}
                    />

                    {/* The page's only title. The screens below open straight onto
                        their content rather than repeating the heading that the
                        operator has just clicked in the sidebar. */}
                    <div className="sb-header__titles">
                        <h1 className="sb-header__title">{page.label}</h1>
                        <div className="sb-header__sub">{page.description}</div>
                    </div>

                    <Space size={12}>
                        <ConnectionPill />
                        <AccountMenu />
                    </Space>
                </Header>

                <Content className="sb-content" id={SCROLL_CONTAINER_ID}>
                    <div className="sb-content__inner">
                        {/* Shown, not swallowed: whatever is on screen underneath is the
                            last good data, and the operator needs to know it is stale
                            rather than current. */}
                        {error && (
                            <Alert
                                type="error"
                                showIcon
                                message="Cannot reach the receiver"
                                description={`${error} Showing the last data that loaded.`}
                                style={{ marginBottom: 16 }}
                            />
                        )}
                        <Routes>
                            <Route path="/" element={<Overview />} />
                            <Route path="/readings" element={<Readings />} />
                            <Route path="/devices" element={<Devices />} />
                            <Route path="/settings" element={<Settings />} />
                            <Route path="*" element={<Navigate to="/" replace />} />
                        </Routes>
                    </div>
                </Content>
            </Layout>
        </Layout>
    );
}

/**
 * Who is signed in, and the way out.
 *
 * Added because there was no way to sign out at all: a session lasted its full 24h
 * TTL and the only escape was clearing browser storage. On a shared counter machine
 * that is not a small omission - the next person to sit down inherits the session.
 *
 * There was a "Forget this computer" item beside it, which cleared the stored
 * WeighBridge pair. Removed: it sat one slip of the mouse away from Sign out, and the
 * cost of the slip was a hunt for the licence certificate before anyone could get back
 * in. The activation is a property of the machine, set once when it is installed, and
 * clearing the browser's site data is the escape hatch on the rare occasion it has to
 * change - which is not an operator's job.
 */
function AccountMenu() {
    const session = useAuthStore((s) => s.session);
    const signOut = useAuthStore((s) => s.signOut);
    const reset = useReadingsStore((s) => s.reset);
    const name = session?.username ?? 'Operator';
    const account = session?.accountName;
    const role = session?.role;

    const items: MenuProps['items'] = [
        {
            key: 'who',
            disabled: true,
            label: (
                <div style={{ padding: '4px 0', lineHeight: 1.45, minWidth: 180 }}>
                    <div style={{ fontWeight: 600, color: BRAND.ink }}>{name}</div>
                    {account && (
                        <div style={{ fontSize: 12, color: BRAND.muted }}>{account}</div>
                    )}
                    {/* Named, because it is the reason half the buttons on Devices
                        are disabled and there was nowhere else to find out. */}
                    {role && (
                        <div style={{ fontSize: 12, color: BRAND.muted, textTransform: 'capitalize' }}>
                            {role}
                        </div>
                    )}
                </div>
            ),
        },
        { type: 'divider' },
        {
            key: 'signout',
            icon: <LogoutOutlined />,
            danger: true,
            label: 'Sign out',
            onClick: () => {
                // The store is cleared as well as the session. Without it the next
                // operator sees the previous shift's weighments for the instant
                // before the first fetch returns.
                reset();
                signOut();
            },
        },
    ];

    return (
        <Dropdown menu={{ items }} placement="bottomRight" trigger={['click']}>
            <Button
                type="text"
                style={{ display: 'flex', alignItems: 'center', gap: 8, paddingInline: 8 }}
            >
                <Avatar size={26} style={{ background: BRAND.primary, fontSize: 12 }}>
                    {name.slice(0, 2).toUpperCase()}
                </Avatar>
                <span style={{ fontSize: 13, maxWidth: 140, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {name}
                </span>
                <DownOutlined style={{ fontSize: 10, color: BRAND.muted }} />
            </Button>
        </Dropdown>
    );
}

function Root() {
    const session = useAuthStore((s) => s.session);
    const unauthorized = useReadingsStore((s) => s.unauthorized);
    const signOut = useAuthStore((s) => s.signOut);

    // A key that stops working - rotated on the receiver, say - sends the operator
    // back to the connect screen rather than leaving a dashboard that silently
    // never updates again.
    useEffect(() => {
        if (unauthorized) signOut();
    }, [unauthorized, signOut]);

    if (!session) return <Login />;
    return <Shell />;
}

export default function App() {
    return (
        <ConfigProvider theme={theme}>
            {/* antd's App wrapper, so message and Modal.confirm inherit the theme
                above rather than rendering with the library defaults - which is
                why a confirm dialog used to arrive with square corners. */}
            <AntApp>
                <HashRouter>
                    <Root />
                </HashRouter>
            </AntApp>
        </ConfigProvider>
    );
}
